import mailApi from './mailApi';

// Get current user's mail account info
export const getMailAccount = async () => {
  const response = await mailApi.get('/api/users/me');
  return response.data;
};

// Get inbox/folder emails
export const getEmails = async (folder = 'inbox', skip = 0, limit = 50, caseId = null) => {
  const params = { folder, skip, limit };
  if (caseId) params.case_id = caseId;
  const response = await mailApi.get('/api/emails/inbox', { params });
  const data = response.data;
  return data;
};

// Get specific email
export const getEmail = async (emailId) => {
  const response = await mailApi.get(`/api/emails/${emailId}`);
  return response.data;
};

// Get public key for an email address
export const getPublicKey = async (email) => {
  const response = await mailApi.get(`/api/emails/keys/${email}`);
  return response.data;
};

// Send email
export const sendEmail = async (emailData) => {
  const response = await mailApi.post('/api/emails/send', emailData);
  return response.data;
};

// Get dashboard stats
export const getDashboardStats = async () => {
  const response = await mailApi.get('/api/emails/stats/dashboard');
  return response.data;
};

// Get folder counts (lightweight)
export const getFolderCounts = async () => {
  const response = await mailApi.get('/api/emails/folders/counts');
  return response.data;
};

// Get templates
export const getTemplates = async (category = null) => {
  const params = category ? { category } : {};
  const response = await mailApi.get('/api/emails/templates/', { params });
  return response.data;
};

// Create template
export const createTemplate = async (templateData) => {
  const response = await mailApi.post('/api/emails/templates/', templateData);
  return response.data;
};

// Update template
export const updateTemplate = async (templateId, templateData) => {
  const response = await mailApi.put(`/api/emails/templates/${templateId}`, templateData);
  return response.data;
};

// Delete template
export const deleteTemplate = async (templateId) => {
  const response = await mailApi.delete(`/api/emails/templates/${templateId}`);
  return response.data;
};

// Render template
export const renderTemplate = async (templateId, variables = {}) => {
  const response = await mailApi.post(`/api/emails/templates/${templateId}/render`, { variables });
  return response.data;
};

// Star/unstar email
export const toggleStar = async (emailId) => {
  const response = await mailApi.patch(`/api/emails/${emailId}/star`);
  return response.data;
};

// Move email to folder
export const moveEmail = async (emailId, folder) => {
  const response = await mailApi.patch(`/api/emails/${emailId}/folder`, { folder });
  return response.data;
};

// Delete email
export const deleteEmail = async (emailId, permanent = false) => {
  const response = await mailApi.delete(`/api/emails/${emailId}`, { 
    params: { permanent } 
  });
  return response.data;
};

// Bulk actions
export const bulkAction = async (emailIds, action, folder = null) => {
  const response = await mailApi.post('/api/emails/bulk-action', {
    email_ids: emailIds,
    action,
    folder
  });
  return response.data;
};

// Get threads
export const getThreads = async (folder = 'inbox', skip = 0, limit = 50) => {
  const response = await mailApi.get('/api/emails/threads/', { 
    params: { folder, skip, limit } 
  });
  return response.data;
};

// Get thread emails
export const getThreadEmails = async (threadId) => {
  const response = await mailApi.get(`/api/emails/threads/${threadId}/emails`);
  return response.data;
};

export const markThreadRead = async (threadId) => {
  const response = await mailApi.post(`/api/emails/threads/${threadId}/mark-read`);
  return response.data;
};

export const markEmailRead = async (emailId, isRead = true) => {
  const response = await mailApi.patch(`/api/emails/${emailId}/read`, null, { params: { is_read: isRead } });
  return response.data;
};

export const searchMatters = async (query, limit = 20) => {
  const response = await mailApi.get('/api/virgo/cases', {
    params: { search: query, limit }
  });
  return response.data;
};


// Advanced search
export const advancedSearch = async (searchParams) => {
  const response = await mailApi.post('/api/emails/search/advanced', searchParams);
  const data = response.data;
  // New paginated format: { results, total, has_more, skip, limit }
  if (data && data.results !== undefined) {
    return data;
  }
  // Legacy fallback
  return { results: Array.isArray(data) ? data : [], total: 0, has_more: false, skip: 0, limit: 50 };
};

// Cancel scheduled email
export const cancelScheduledEmail = async (emailId) => {
  const response = await mailApi.post(`/api/emails/${emailId}/cancel`);
  return response.data;
};

// AI Analysis endpoints
export const getEmailAIAnalysis = async (emailId) => {
  const response = await mailApi.get(`/api/emails/${emailId}/ai-analysis`);
  return response.data;
};

export const analyzeEmailBatch = async (limit = 100) => {
  const response = await mailApi.post('/api/emails/ai-analyze-batch', null, { params: { limit } });
  return response.data;
};

export const getAIStats = async () => {
  const response = await mailApi.get('/api/emails/ai-stats');
  return response.data;
};

export const linkEmailToCase = async (emailId, caseId, feedback = 'accept') => {
  const response = await mailApi.post(`/api/emails/${emailId}/link-case`, { 
    case_id: caseId,
    feedback 
  });
  return response.data;
};

export const submitAIFeedback = async (emailId, feedback, notes = null) => {
  const response = await mailApi.post(`/api/emails/${emailId}/feedback`, {
    feedback,
    notes
  });
  return response.data;
};

export const logAIFeedback = async (emailId, feedbackType, feedbackAction, options = {}) => {
  const params = new URLSearchParams({
    feedback_type: feedbackType,
    feedback_action: feedbackAction,
    ...options
  });
  const response = await mailApi.post(`/api/emails/${emailId}/ai-feedback?${params.toString()}`);
  return response.data;
};

// Train Astraea on emails (RAG embedding update)
export const trainAstraeaOnMail = async (folder = null) => {
  const response = await mailApi.post('/api/mail/train-astraea', { folder });
  return response.data;
};

// Get training status
export const getAstraeaTrainingStatus = async () => {
  const response = await mailApi.get('/api/mail/train-astraea/status');
  return response.data;
};

// Get deleted emails
export const getDeletedEmails = async (skip = 0, limit = 50) => {
  const response = await mailApi.get('/api/emails/inbox', {
    params: { folder: 'trash', skip, limit }
  });
  return response.data.emails || [];
};

// Restore deleted email
export const restoreEmail = async (emailId) => {
  const response = await mailApi.post(`/api/emails/${emailId}/restore`);
  return response.data;
};


// Get email attachments
export const getEmailAttachments = async (emailId) => {
  const response = await mailApi.get(`/api/emails/${emailId}/attachments`);
  return response.data;
};

// Download attachment
export const downloadAttachment = async (emailId, attachmentId, filename) => {
  const response = await mailApi.get(`/api/emails/${emailId}/attachments/${attachmentId}/download`, {
    responseType: 'blob'
  });
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'attachment');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
// Permanently delete email
export const permanentlyDeleteEmail = async (emailId) => {
  const response = await mailApi.delete(`/api/emails/${emailId}`, {
    params: { permanent: true }
  });
  return response.data;
};

// ============================================================================
// Compose & Settings Service Functions
// ============================================================================

// Suggest recipients
export const suggestRecipients = async (query) => {
  const response = await mailApi.get('/api/mail/compose/recipients/suggest', {
    params: { q: query, limit: 10 }
  });
  return response.data;
};

// Get signature
export const getSignature = async () => {
  const response = await mailApi.get('/api/mail/settings/signature');
  return response.data;
};

// Check conflicts
export const checkConflicts = async (recipients) => {
  const response = await mailApi.post('/api/mail/compose/conflict-check', { recipients });
  return response.data;
};

// Get forward attachments
export const getForwardAttachments = async (emailId) => {
  const response = await mailApi.get(`/api/mail/compose/forward-attachments/${emailId}`);
  return response.data;
};

// Generate AI draft
export const generateDraft = async (payload) => {
  const response = await mailApi.post('/api/mail/compose/generate-draft', payload);
  return response.data;
};

// Send email (FormData)
export const sendComposedEmail = async (formData) => {
  const response = await mailApi.post('/api/mail/compose/send', formData, {
    headers: { 'Content-Type': undefined }
  });
  return response.data;
};

// ============================================================================
// Mail Settings Service Functions
// ============================================================================

// Load mail settings (signature)
export const loadMailSettings = async () => {
  const response = await mailApi.get('/api/user/mail-settings/signature');
  return response.data;
};

// Save signature
export const saveSignature = async (payload) => {
  const response = await mailApi.put('/api/user/mail-settings/signature', payload);
  return response.data;
};

// Get away message
export const getAwayMessage = async () => {
  const response = await mailApi.get('/api/away-messages/');
  return response.data;
};

// Save away message
export const saveAwayMessageApi = async (payload) => {
  const response = await mailApi.post('/api/away-messages/', payload);
  return response.data;
};

// Delete away message
export const deleteAwayMessage = async () => {
  const response = await mailApi.delete('/api/away-messages/');
  return response.data;
};

// ============================================================================
// Custom Folder (Label) Service Functions
// ============================================================================

// List all custom folders (flat)
export const listCustomFolders = async () => {
  const response = await mailApi.get('/api/email-folders/folders/flat');
  return response.data;
};

// Get recently used folders
export const getRecentFolders = async () => {
  const response = await mailApi.get('/api/email-folders/folders/recent', {
    params: { limit: 5 }
  });
  return response.data;
};

// Create a custom folder
export const createFolder = async (name, options = {}) => {
  const response = await mailApi.post('/api/email-folders/folders', {
    name,
    parent_id: options.parent_id || null,
    color: options.color || null,
    icon: options.icon || null
  });
  return response.data;
};

// Rename a custom folder
export const renameFolder = async (id, name) => {
  const response = await mailApi.put(`/api/email-folders/folders/${id}`, { name });
  return response.data;
};

// Update a custom folder (color, icon, parent_id, sort_order)
export const updateFolder = async (id, updates) => {
  const response = await mailApi.put(`/api/email-folders/folders/${id}`, updates);
  return response.data;
};

// Delete a custom folder
export const deleteFolder = async (id) => {
  const response = await mailApi.delete(`/api/email-folders/folders/${id}`);
  return response.data;
};

// Move email to a custom folder
export const moveToCustomFolder = async (emailId, folderId) => {
  const response = await mailApi.post('/api/email-folders/emails/move', {
    email_id: emailId,
    folder_id: folderId
  });
  return response.data;
};

// Remove email from custom folder
export const removeFromCustomFolder = async (emailId) => {
  const response = await mailApi.post(`/api/email-folders/emails/${emailId}/unsave`);
  return response.data;
};

// Get emails in a custom folder
export const getCustomFolderEmails = async (folderId, skip = 0, limit = 50) => {
  const response = await mailApi.get(`/api/email-folders/folders/${folderId}/emails`, {
    params: { skip, limit }
  });
  return response.data;
};

// ============================================================================
// Mail Preferences Service Functions
// ============================================================================

export const getMailPreferences = async () => {
  const response = await mailApi.get('/api/mail/preferences');
  return response.data;
};

export const updateMailPreferences = async (prefs) => {
  const response = await mailApi.put('/api/mail/preferences', prefs);
  return response.data;
};

// ============================================================================
// Blocked Senders Service Functions
// ============================================================================

export const getBlockedSenders = async () => {
  const response = await mailApi.get('/api/mail/blocked-senders');
  return response.data;
};

export const addBlockedSender = async (data) => {
  const response = await mailApi.post('/api/mail/blocked-senders', data);
  return response.data;
};

export const removeBlockedSender = async (id) => {
  const response = await mailApi.delete(`/api/mail/blocked-senders/${id}`);
  return response.data;
};

// ============================================================================
// Firm Settings (read-only for non-admin)
// ============================================================================

export const getFirmSetting = async (key) => {
  const response = await mailApi.get(`/api/firm-settings/${key}`);
  return response.data;
};
